import Konva from "konva";

/**
 * Canvas Konva de la page « Concevoir la navigation ».
 *
 * Chaque point est un Konva.Group dont l'ORIGINE est le centre du point :
 * la position du groupe sur le stage EST la position du point (décalée de
 * imageOffsetX pour le centrage horizontal de l'image de fond). Tous les
 * enfants (forme, label, illustration, halo) sont en coordonnées relatives,
 * si bien qu'un drag déplace tout d'un bloc.
 *
 * Callbacks attendus :
 * - getResourceUrl(resourceId) → Promise<string> (URL S3 du fichier)
 * - onPointClick(pointId)
 * - onPointDragEnd(pointId, { x, y })  — x/y en coordonnées image (offset déduit)
 * - onBackgroundClick({ x, y } | null) — null = clic fond hors mode placement (désélection)
 * - onPointHover(pointId | null)
 */
export function usePointsCanvas({ getResourceUrl, onPointClick, onPointDragEnd, onBackgroundClick, onPointHover }) {
    let stage = null;
    let layer = null;
    let addMode = false;
    let showLabels = true;
    let selectedPointId = null;
    let imageOffsetX = 0;
    let renderVersion = 0;

    const loadImageElement = (src) => new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
    });

    const setCursor = (cursor) => {
        if (stage) stage.container().style.cursor = cursor;
    };

    // ---- construction des nœuds -------------------------------------------

    // Forme du point (cercle / carré / image), centrée sur (0,0).
    const buildShapeNode = async (point, pointSize) => {
        const pointShape = point.shape || "circle";
        if (pointShape === "square") {
            return new Konva.Rect({
                name: "shape",
                x: -pointSize,
                y: -pointSize,
                width: pointSize * 2,
                height: pointSize * 2,
                fill: point.image_position === "inside" ? null : point.color,
                stroke: "white",
                strokeWidth: 2,
            });
        }
        if (pointShape === "image" && point.shape_image_id) {
            const maxDim = pointSize * 2;
            const imgObj = await loadImageElement(await getResourceUrl(point.shape_image_id));
            const ratio = imgObj.width / imgObj.height;
            const w = ratio >= 1 ? maxDim : maxDim * ratio;
            const h = ratio >= 1 ? maxDim / ratio : maxDim;
            return new Konva.Image({
                name: "shape",
                x: 0,
                y: 0,
                offsetX: w / 2,
                offsetY: h / 2,
                image: imgObj,
                width: w,
                height: h,
            });
        }
        return new Konva.Circle({
            name: "shape",
            x: 0,
            y: 0,
            radius: pointSize,
            fill: point.image_position === "inside" ? null : point.color,
            stroke: "white",
            strokeWidth: 2,
        });
    };

    // Illustration « inside » : l'image remplit la forme (COVER : recadrage
    // centré via Math.min) ; un fond de la couleur du point est placé derrière.
    const applyInsideImage = (group, shapeNode, imageObj, point, pointSize) => {
        const scale = (pointSize * 2) / Math.min(imageObj.width, imageObj.height);
        shapeNode.fillPatternImage(imageObj);
        shapeNode.fillPatternRepeat("no-repeat");
        shapeNode.fillPatternScaleX(scale);
        shapeNode.fillPatternScaleY(scale);
        shapeNode.fillPatternOffset({ x: imageObj.width / 2, y: imageObj.height / 2 });
        let backing;
        if ((point.shape || "circle") === "square") {
            // Rect : l'origine locale (0,0) est le coin haut-gauche → recentrer le motif.
            shapeNode.fillPatternX(pointSize);
            shapeNode.fillPatternY(pointSize);
            backing = new Konva.Rect({
                name: "backing",
                x: -pointSize,
                y: -pointSize,
                width: pointSize * 2,
                height: pointSize * 2,
                fill: point.color,
            });
        } else {
            backing = new Konva.Circle({
                name: "backing",
                x: 0,
                y: 0,
                radius: pointSize,
                fill: point.color,
            });
        }
        group.add(backing);
        backing.moveToBottom();
    };

    // Illustration à côté du point (top/right/bottom/left), 50 px max,
    // ratio préservé. Mêmes constantes que l'ancien showCircles.
    const buildSideIllustration = (imageObj, point, pointSize, label) => {
        const maxDim = 50;
        const half = 25;
        const ratio = imageObj.width / imageObj.height;
        const w = ratio >= 1 ? maxDim : maxDim * ratio;
        const h = ratio >= 1 ? maxDim / ratio : maxDim;
        let x = 0;
        let y = 0;
        switch (point.image_position) {
            case "top":
                y = -pointSize - (half * 2) - 10;
                x = -half;
                label.y(pointSize + 15); // le label passe sous le point
                break;
            case "right":
                x = pointSize + 10;
                y = -half;
                break;
            case "bottom":
                y = pointSize + 10;
                x = -half;
                break;
            case "left":
                x = -pointSize - (half * 2) - 10;
                y = -half;
                break;
        }
        return new Konva.Image({
            name: "illustration",
            x,
            y,
            image: imageObj,
            width: w,
            height: h,
            stroke: "white",
            strokeWidth: 2,
            shadowColor: "black",
            shadowBlur: 10,
            shadowOffsetX: 3,
            shadowOffsetY: 3,
            shadowOpacity: 0.6,
            filters: [Konva.Filters.Contrast],
            contrast: 10,
        });
    };

    const buildPointGroup = async (point) => {
        const pointSize = point.size || 10;
        const group = new Konva.Group({
            id: "point_" + point.id,
            x: point.x + imageOffsetX,
            y: point.y,
            draggable: true,
        });
        let shapeNode;
        try {
            shapeNode = await buildShapeNode(point, pointSize);
        } catch (error) {
            console.log(error);
            shapeNode = await buildShapeNode({ ...point, shape: "circle" }, pointSize);
        }
        group.add(shapeNode);
        const label = new Konva.Text({
            name: "label",
            x: -50,
            y: -pointSize - 25,
            text: point.name,
            fontSize: point.label_size || 16,
            fontFamily: point.label_font || "Arial",
            fill: point.label_color || "white",
            shadowColor: "black",
            shadowBlur: 4,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
            shadowOpacity: 0.7,
            align: "center",
            visible: showLabels,
        });
        if (point.resource_id) {
            try {
                const imageObj = await loadImageElement(await getResourceUrl(point.resource_id));
                if (point.image_position === "inside") {
                    if (typeof shapeNode.fillPatternImage === "function") {
                        applyInsideImage(group, shapeNode, imageObj, point, pointSize);
                    }
                } else if (point.image_position) {
                    group.add(buildSideIllustration(imageObj, point, pointSize, label));
                }
            } catch (error) {
                console.log(error);
            }
        }
        group.add(label);

        group.on("click tap", (e) => {
            e.cancelBubble = true;
            if (onPointClick) onPointClick(point.id);
        });
        group.on("mouseenter", () => {
            setCursor("pointer");
            if (onPointHover) onPointHover(point.id);
        });
        group.on("mouseleave", () => {
            setCursor(addMode ? "crosshair" : "default");
            if (onPointHover) onPointHover(null);
        });
        group.on("dragend", () => {
            if (onPointDragEnd) {
                onPointDragEnd(point.id, {
                    x: Math.max(1, Math.round(group.x() - imageOffsetX)),
                    y: Math.max(1, Math.round(group.y())),
                });
            }
        });
        return group;
    };

    // ---- halos (sélection / survol) ----------------------------------------

    const removeRing = (group, name) => {
        const ring = group.findOne("." + name);
        if (ring) ring.destroy();
    };

    const setRing = (group, name, color) => {
        removeRing(group, name);
        const box = group.getClientRect({ relativeTo: group, skipShadow: true });
        group.add(new Konva.Rect({
            name,
            x: box.x - 4,
            y: box.y - 4,
            width: box.width + 8,
            height: box.height + 8,
            stroke: color,
            strokeWidth: 2,
            dash: [6, 4],
            listening: false,
        }));
    };

    const findGroup = (pointId) => (layer ? layer.findOne("#point_" + pointId) : null);

    // ---- API publique --------------------------------------------------------

    // (Re)crée le stage avec l'image de fond. Dimensionnement historique :
    // image ramenée à 500 px de large, stage de maxWidth, image centrée.
    const initStage = ({ containerId, imgUrl, maxWidth }) => new Promise((resolve, reject) => {
        const imageObj = new Image();
        imageObj.onerror = reject;
        imageObj.onload = () => {
            const imageWidth = 500;
            const imageHeight = imageObj.height * (imageWidth / imageObj.width);
            if (stage) stage.destroy();
            stage = new Konva.Stage({ container: containerId, width: maxWidth, height: imageHeight });
            layer = new Konva.Layer();
            stage.add(layer);
            imageOffsetX = (maxWidth - imageWidth) / 2;
            layer.add(new Konva.Image({
                name: "background",
                x: imageOffsetX,
                y: 0,
                image: imageObj,
                width: imageWidth,
                height: imageHeight,
            }));
            layer.draw();
            // Clic sur le fond uniquement : les groupes de points annulent la
            // propagation (cancelBubble).
            stage.on("click tap", () => {
                if (!addMode) {
                    if (onBackgroundClick) onBackgroundClick(null);
                    return;
                }
                const pos = stage.getPointerPosition();
                if (!pos) return;
                if (onBackgroundClick) {
                    onBackgroundClick({
                        x: Math.max(1, Math.round(pos.x - imageOffsetX)),
                        y: Math.max(1, Math.round(pos.y)),
                    });
                }
            });
            resolve();
        };
        imageObj.src = imgUrl;
    });

    // Redessine tous les points. Les nouveaux groupes sont construits AVANT de
    // détruire les anciens (swap atomique) : pas de flash pendant le chargement
    // des images, et updatePoint/removePoint restent cohérents si un rendu est
    // en cours. Garde anti-course : si un render plus récent démarre, l'ancien
    // s'abandonne.
    const renderPoints = async (points) => {
        const version = ++renderVersion;
        if (!layer) return;
        const list = Array.isArray(points) ? points : [];
        const groups = await Promise.all(list.map(async (point) => {
            try {
                return await buildPointGroup(point);
            } catch (error) {
                console.log(error);
                return null;
            }
        }));
        if (version !== renderVersion || !layer) return;
        layer.getChildren((node) => node.getClassName() === "Group").slice().forEach((g) => g.destroy());
        groups.filter(Boolean).forEach((g) => layer.add(g));
        if (selectedPointId) selectPoint(selectedPointId);
        layer.draw();
    };

    // Redessine UN point (aperçu live ou après sauvegarde) sans toucher aux
    // autres. Conserve la position à l'écran du groupe présent au moment de
    // l'ajout. Invalide tout rendu complet en cours : ses données seraient
    // périmées, et son swap pourrait créer un doublon de ce point.
    const updatePoint = async (point) => {
        if (!layer) return;
        const version = ++renderVersion;
        const group = await buildPointGroup(point);
        if (version !== renderVersion || !layer) return;
        const existing = findGroup(point.id);
        if (existing) {
            group.position(existing.position());
            existing.destroy();
        }
        layer.add(group);
        if (selectedPointId === point.id) setRing(group, "selection", "#1976d2");
        layer.draw();
    };

    const removePoint = (pointId) => {
        // Invalide tout rendu complet en cours : il pourrait ressusciter le point.
        renderVersion++;
        const group = findGroup(pointId);
        if (group) group.destroy();
        if (selectedPointId === pointId) selectedPointId = null;
        if (layer) layer.draw();
    };

    // Replace le groupe d'un point à une position donnée (coordonnées image).
    // Contrairement à updatePoint, ne reconstruit rien et ne préserve PAS la
    // position à l'écran — c'est le retour arrière d'un drag dont la
    // sauvegarde a échoué.
    const movePoint = (pointId, x, y) => {
        const group = findGroup(pointId);
        if (!group || !layer) return;
        group.position({ x: x + imageOffsetX, y });
        layer.draw();
    };

    const selectPoint = (pointId) => {
        if (!layer) return;
        if (selectedPointId && selectedPointId !== pointId) {
            const prev = findGroup(selectedPointId);
            if (prev) removeRing(prev, "selection");
        }
        selectedPointId = pointId;
        if (pointId) {
            const group = findGroup(pointId);
            if (group) {
                removeRing(group, "hover");
                setRing(group, "selection", "#1976d2");
            }
        }
        layer.draw();
    };

    const highlightPoint = (pointId, on) => {
        if (!layer) return;
        const group = findGroup(pointId);
        if (!group || pointId === selectedPointId) return;
        if (on) setRing(group, "hover", "#9e9e9e");
        else removeRing(group, "hover");
        layer.draw();
    };

    const setAddMode = (on) => {
        addMode = on;
        setCursor(on ? "crosshair" : "default");
    };

    const setShowLabels = (on) => {
        showLabels = on;
        if (!layer) return;
        layer.find(".label").forEach((label) => label.visible(on));
        layer.draw();
    };

    const destroy = () => {
        if (stage) stage.destroy();
        stage = null;
        layer = null;
        selectedPointId = null;
        addMode = false;
        showLabels = true;
        renderVersion++;
    };

    return {
        initStage,
        renderPoints,
        updatePoint,
        removePoint,
        movePoint,
        selectPoint,
        highlightPoint,
        setAddMode,
        setShowLabels,
        destroy,
    };
}
